import PropTypes from "prop-types";
import React, { useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { Form } from "react-bootstrap";
import { isMobile } from "react-device-detect";
import styled, { css } from "styled-components";
import { error as errorNotif } from "react-notification-system-redux";

import { ordersActions } from "_store/actions";
import breakpoints from "_constants/breakpoints";
import { PRIVACY_PRODUCTS_CODES } from "_constants";
import { getFileExtensionsString, getFileExtension, checkIfFileAllowed, sanitizeFileName, validateUrl } from "_helpers";

import { ReactComponent as DownloadSVG } from "assets/img/Download.svg";

import Table from "containers/Table/Table";
import { SpinnerBlockWrapper } from "widget/Spinner";

const UPLOADED_WIDTH = 130;
const OPEN_WIDTH = 80;

const Container = styled.div`
  position: relative;
  width: 100%;
  max-width: 1364px;
  padding: 36px 32px 36px;
  margin-bottom: 24px;
  background: #ffffff;
  box-shadow: 0px 3px 25px rgba(168, 168, 168, 0.3);

  ${({ theme: { mobile } }) =>
    mobile &&
    css`
      @media (max-width: ${breakpoints.mobileM}) {
        padding: 24px 12px 12px;
      }
    `}
`;
const UploadForm = styled(Form)`
  margin-top: 20px;
`;
const Group = styled(Form.Group)`
  &:not(:last-child) {
    margin-bottom: 18px;
  }
`;
const Label = styled(Form.Label)`
  height: auto;
  margin-left: 8px;
  margin-bottom: 0;
  font-family: "Montserrat";
  font-style: normal;
  font-weight: bold;
  font-size: 14px;
  line-height: 28px;
  display: flex;
  align-items: center;

  @media (max-width: ${breakpoints.mobileM}) {
    line-height: 16px;
    font-size: 14px;
    margin-bottom: 8px;
  }
`;
const Control = styled(Form.Control)`
  font-family: "Montserrat";
  font-size: 14px;
  font-weight: 400;
  line-height: 24px;
  border: 1px solid #a5aaaf;
  &:not(:last-child) {
    margin-bottom: 12px;
  }
`;
const ActionButtonsContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  flex-direction: row;
  flex-wrap: wrap;
  gap: 12px;
  border-top: 1px solid lightgray;
  padding-top: 12px;
  margin-top: 36px;

  ${({ theme: { mobile } }) =>
    mobile
      ? css`
          flex-direction: column;
        `
      : css`
          @media (max-width: 700px) {
            flex-direction: column;
            justify-content: center;
            align-items: stretch;
            gap: 12px;
          }
        `}
`;

const Buttons = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;

  @media (max-width: 991px) {
    flex-direction: column;
    justify-content: center;
    align-items: stretch;
    gap: 12px;
  }
`;
const FileInput = styled.input`
  display: none;
`;
const Button = styled.button`
  min-width: 150px;
  height: 42px;
  background: #f37217;
  border: none;
  border-radius: 4px;
  font-style: normal;
  font-weight: 700;
  font-size: 14px;
  line-height: 24px;
  color: #fafaff;

  &:disabled {
    background-color: #7b848d;
  }

  @media (max-width: ${breakpoints.mobileL}) {
    min-width: auto;
    width: 100%;
  }
`;

const ClearButton = styled.button`
  min-width: 24px;
  max-width: 24px;
  min-height: 24px;
  max-height: 24px;
  border-radius: 4px;
  font-style: normal;
  font-weight: 700;
  font-size: 12px;
  margin-left: 12px;
  margin-top: 12px;
`;
const FileName = styled.div`
  text-overflow: ellipsis;
  overflow-x: hidden;
  padding: 12px 24px 12px 12px;

  @media (max-width: 991px) {
    padding: 12px 8px;
  }
`;
const Wrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;

  @media (max-width: ${breakpoints.mobileL}) {
    flex-direction: column;
    margin-bottom: 32px;
    width: 100%;
  }

  ${({ $left }) =>
    $left &&
    css`
      justify-content: start;
    `}

  ${({ $right }) =>
    $right &&
    css`
      justify-content: end;
    `}
`;
const AssessmentContainer = styled.div`
  border-top: 1px solid lightgray;
  padding: 8px;
  margin-top: 36px;
  border-radius: 4px;
  text-align: center;

  color: #0645ad;

  ${({ isReady }) =>
    isReady &&
    css`
      cursor: pointer;
      opacity: 1;
    `}
`;

const AssessmentText = styled.span`
  &:hover {
    text-decoration: underline;
  }

  ${({ isReady }) =>
    !isReady &&
    css`
      color: #3366bb;

      &:hover {
        text-decoration: none;
      }
    `}
`;

const AssessmentIndicator = styled.span`
  color: var(--bs-red);
  text-decoration: none;

  ${({ isReady }) =>
    isReady &&
    css`
      color: var(--bs-green);
    `}
`;

const FlexContainer = styled.div`
  display: flex;
  max-width: 250px;
  white-space: nowrap;

  @media (max-width: 530px) {
    max-width: 200px;
  }

  @media (max-width: ${breakpoints.mobileL}) {
    max-width: 100%;
  }
`;
const EmptyMessage = styled.div`
  color: #7b848d;
  text-align: center;
  font-weight: bold;
`;

function Documents({
  orderId,
  product,
  emptyMessage,
  files = [],
  assessments,
  actions,
  isDisabled = false,
  showCancel,
  onChange,
  onUpload,
  onUpdate,
  handleCancelCallback,
}) {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { fileTypes } = useSelector(({ constant }) => constant);

  const fileInputRef = useRef(null);

  const [file, setFile] = useState(null);
  const [validated, setValidated] = useState(false);

  const specimanFileType = fileTypes[product?.code?.code]?.TRADEMARK_SPECIMEN;
  const specimanFileExtensions = getFileExtensionsString(specimanFileType?.allowedExtensions);
  const selectedFileExt = getFileExtension(file?.name);
  // const isExtensionAllowed = specimanFileType?.allowedExtensions?.includes(selectedFileExt);
  const isExtensionAllowed = checkIfFileAllowed(selectedFileExt, specimanFileType?.allowedExtensions);

  const checkErrors = () => {
    const { website, specimenDescription } = product;

    if (!website && !specimenDescription && !file) {
      return "Enter the URL or upload specimen (or both of them).";
    }

    if (!specimenDescription && file) {
      return "Please provide a specimen description or clear selected file.";
    }

    // if (specimenDescription && !file) {
    //   return "Please select a file or clear specimen description.";
    // }

    if (specimenDescription && files.length < 1 && !file) {
      return "Please select a file or clear specimen description.";
    }

    if (website && !validateUrl(website)) {
      return "Please provide correct Website URL in format:\nhttps://www.example.com";
    }

    return null;
  };

  const clearFile = () => {
    setFile(null);
    if (fileInputRef.current) fileInputRef.current.value = null;
  };

  const handleSubmit = (e) => {
    const form = e.currentTarget;

    e.preventDefault();
    e.stopPropagation();

    const error = checkErrors();

    if (error) {
      dispatch(
        errorNotif({
          title: "Error",
          message: error,
          autoDismiss: 12,
        })
      );
    } else {
      if (form.checkValidity() !== false) {
        const formData = new FormData();

        if (file) {
          if (isExtensionAllowed) {
            formData.append("file", file, sanitizeFileName(file?.name));
            onUpload(formData);
          } else {
            dispatch(
              errorNotif({
                title: "Error",
                message: "Please select file with correct extension: " + (specimanFileExtensions || "any type"),
              })
            );
            clearFile();
          }
        } else {
          onUpdate();
        }

        clearFile();
      }

      setValidated(true);
    }
  };

  const handleClickFileInput = (e) => {
    e.preventDefault();
    e.stopPropagation();

    fileInputRef.current.click();
  };

  const handleChangeFile = (event) => {
    if (!event.target.files.length) {
      clearFile();
      return;
    }

    const formFileExt = getFileExtension(event.target.files[0]?.name);
    const isCurrentExtensionAllowed = specimanFileType?.allowedExtensions?.includes(formFileExt);
    if (isCurrentExtensionAllowed) {
      const fileSize = event.target.files[0]?.size;
      if (fileSize && fileSize / 1024 / 1024 <= 5) {
        setFile(event.target.files[0]);
      } else {
        dispatch(errorNotif({ title: "Error", message: "File size limit is 5MB" }));
        clearFile();
      }
    } else {
      dispatch(
        errorNotif({ title: "Error", message: "Please select file with correct extension: " + specimanFileExtensions })
      );
      clearFile();
    }
  };

  const handleDownload = (fileProps) => {
    const { productId, filename, fileId } = fileProps;

    if (productId) {
      dispatch(ordersActions.getFileFromProduct(orderId, productId, filename, true));
    } else {
      dispatch(ordersActions.getFile(orderId, fileId, true));
    }
  };

  const columns = isMobile
    ? [
        { label: "NAME/UPLOADED", key: [{ key: "filename" }, { key: "uploaded", format: "MMM DD, YYYY" }] },
        { label: "OPEN", key: "", width: OPEN_WIDTH, element: DownloadSVG, onClick: handleDownload },
      ]
    : [
        { label: "OPEN", key: "", width: OPEN_WIDTH, element: DownloadSVG, onClick: handleDownload },
        { label: "NAME", key: "filename" },
        { label: "UPLOADED", key: "uploaded", width: UPLOADED_WIDTH, format: "MMM DD, YYYY" },
      ];

  const columnsMail = isMobile
    ? [
        {
          label: "MAIL INFO",
          key: [{ key: "customerName" }, { key: "uploaded", format: "MMM DD, YYYY" }, { key: "note" }],
        },
        { label: "OPEN", key: "", width: OPEN_WIDTH, element: DownloadSVG, onClick: handleDownload },
      ]
    : [
        { label: "OPEN", key: "", width: OPEN_WIDTH, element: DownloadSVG, onClick: handleDownload },
        { label: "NAME", key: "customerName", width: 150 },
        { label: "NOTE", key: "note", minWidthCell: 150 },
        { label: "UPLOADED", key: "uploaded", width: UPLOADED_WIDTH, format: "MMM DD, YYYY" },
      ];

  if (!orderId) return null;

  return (
    <Container>
      {files?.length ? (
        <Table
          data={files}
          columns={PRIVACY_PRODUCTS_CODES.includes(product?.code?.code) ? columnsMail : columns}
          contentMaxHeight={isMobile ? "auto" : "150px"}
        />
      ) : (
        <EmptyMessage>{emptyMessage ? emptyMessage : "No documents for this order"}</EmptyMessage>
      )}
      {actions === "SPECIMEN" && (
        <UploadForm noValidate validated={validated} onSubmit={handleSubmit}>
          <Group>
            <Label>Enter the URL of the website, if you have one</Label>
            <Control
              type="text"
              name="website"
              placeholder="https://www.example.com"
              value={product?.website || ""}
              onChange={onChange}
            />
          </Group>
          <Group>
            <Label>Or</Label>
          </Group>
          <Group>
            <Label>Enter description of the specimen and upload file ({specimanFileExtensions || "any type"})</Label>
            <Control
              type="text"
              name="specimenDescription"
              value={product?.specimenDescription || ""}
              onChange={onChange}
            />
            <Form.Control.Feedback type="invalid">
              Please provide a specimen description and select a file.
            </Form.Control.Feedback>
          </Group>
          <Buttons>
            <Wrapper $left>
              <Button onClick={handleClickFileInput}>Select file</Button>
              <FlexContainer>
                <FileInput ref={fileInputRef} type="file" accept=".pdf" onChange={handleChangeFile} />
                {file && (
                  <ClearButton title="Clear selected file" onClick={() => clearFile()}>
                    X
                  </ClearButton>
                )}
                <FileName title={file?.name}>{file?.name || "<No File selected>"}</FileName>
              </FlexContainer>
            </Wrapper>
            <Wrapper $right>
              {showCancel && handleCancelCallback && (
                <Button type="button" onClick={handleCancelCallback}>
                  Cancel
                </Button>
              )}
              <Button type="submit">Save</Button>
            </Wrapper>
          </Buttons>
        </UploadForm>
      )}

      {assessments?.isProductExist && (
        <AssessmentContainer isReady={assessments?.isReady}>
          <AssessmentText
            isReady={assessments?.isReady}
            onClick={() => {
              if (assessments?.isReady) {
                navigate(`/orders/${orderId}/assessment`);
              }
            }}
          >
            Legal Team Trademark Assessment Report
          </AssessmentText>{" "}
          <AssessmentIndicator isReady={assessments?.isReady}>
            {assessments?.isReady ? "(Ready)" : "(Not Ready)"}
          </AssessmentIndicator>
        </AssessmentContainer>
      )}

      {actions === "LOGO_DESIGN_SERVICE" && files?.length > 0 && (
        <ActionButtonsContainer>
          <Button onClick={() => navigate(`/orders/${orderId}/revision`)}>Logo Design Results</Button>
        </ActionButtonsContainer>
      )}

      {isDisabled && <SpinnerBlockWrapper />}
    </Container>
  );
}

Documents.propTypes = {
  order: PropTypes.object,
  orderId: PropTypes.string.isRequired,
  product: PropTypes.object,
  productId: PropTypes.string,
  emptyMessage: PropTypes.string,
  files: PropTypes.arrayOf(PropTypes.object),
  showCancel: PropTypes.bool,
  actions: PropTypes.string,
  isDisabled: PropTypes.bool,
  assessments: PropTypes.object,
  onChange: PropTypes.func,
  onUpload: PropTypes.func,
  onUpdate: PropTypes.func,
  handleCancelCallback: PropTypes.func,
};

export default Documents;
